import { unstable_useTriggerPopoverAriaProps, useAui } from '@assistant-ui/react'
import { LexicalComposerInput, type LexicalComposerInputProps } from '@assistant-ui/react-lexical'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { COMMAND_PRIORITY_CRITICAL, COMMAND_PRIORITY_HIGH, KEY_ENTER_COMMAND, PASTE_COMMAND } from 'lexical'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, type ReactNode } from 'react'
import {
  attachClipboardFiles,
  collectClipboardFiles,
  getClipboardDataFromPasteEvent,
  nameClipboardFiles,
  shouldAttachClipboardFilesOnPaste,
  shouldBlockDmpChatLexicalSubmit,
  syncDmpChatLexicalInputAccessibility,
  type DmpChatClipboardPasteEvent,
} from '../dmpChatLexicalComposer.ts'

export interface DmpChatComposerInputHandle {
  focus: () => void
}

export interface DmpChatLexicalInputProps extends Omit<LexicalComposerInputProps, 'children' | 'ref'> {
  accessibleName: string
  describedBy?: string
  disabled: boolean
  sendDisabled: boolean
  children?: ReactNode
}

function DmpChatLexicalBridgePlugin({
  disabled,
  sendDisabled,
}: Pick<DmpChatLexicalInputProps, 'disabled' | 'sendDisabled'>) {
  const [editor] = useLexicalComposerContext()
  const aui = useAui()

  useEffect(() => {
    editor.setEditable(!disabled)
  }, [disabled, editor])

  useEffect(() => {
    const submitDisabled = disabled || sendDisabled
    if (!submitDisabled) {
      return
    }

    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event: KeyboardEvent | null) => {
        if (event === null || !shouldBlockDmpChatLexicalSubmit(event, submitDisabled)) {
          return false
        }

        event.preventDefault()
        return true
      },
      COMMAND_PRIORITY_CRITICAL
    )
  }, [disabled, editor, sendDisabled])

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const clipboardEvent = asClipboardPasteEvent(event)
        if (!clipboardEvent) {
          return false
        }

        const files = nameClipboardFiles(collectClipboardFiles(getClipboardDataFromPasteEvent(clipboardEvent)))
        if (
          !shouldAttachClipboardFilesOnPaste({
            attachmentsSupported: aui.thread.getState().capabilities.attachments === true,
            disabled,
            files,
          })
        ) {
          return false
        }

        clipboardEvent.preventDefault()
        void attachClipboardFiles(files, (file) => aui.composer.addAttachment(file))
        return true
      },
      COMMAND_PRIORITY_HIGH
    )
  }, [aui, disabled, editor])

  return null
}

function asClipboardPasteEvent(event: unknown): DmpChatClipboardPasteEvent | null {
  if (typeof event !== 'object' || event === null) {
    return null
  }

  if (typeof (event as { preventDefault?: unknown }).preventDefault !== 'function') {
    return null
  }

  return event as DmpChatClipboardPasteEvent
}

export const DmpChatLexicalInput = forwardRef<DmpChatComposerInputHandle, DmpChatLexicalInputProps>(
  function DmpChatLexicalInput(
    { accessibleName, describedBy, disabled, sendDisabled, children, placeholder, ...props },
    ref
  ) {
    const containerRef = useRef<HTMLDivElement>(null)
    const triggerAriaProps = unstable_useTriggerPopoverAriaProps()
    const getContentEditable = useCallback(
      () => containerRef.current?.querySelector<HTMLElement>('[contenteditable]') ?? null,
      []
    )
    const syncAccessibility = useCallback(() => {
      const contentEditable = getContentEditable()
      if (!contentEditable) {
        return
      }

      syncDmpChatLexicalInputAccessibility(contentEditable, {
        accessibleName,
        describedBy,
        disabled,
        placeholder,
        triggerAriaProps,
      })
    }, [accessibleName, describedBy, disabled, getContentEditable, placeholder, triggerAriaProps])

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          getContentEditable()?.focus()
        },
      }),
      [getContentEditable]
    )

    useEffect(() => {
      syncAccessibility()
      const container = containerRef.current
      if (!container || typeof MutationObserver === 'undefined') {
        return
      }

      const observer = new MutationObserver(syncAccessibility)
      observer.observe(container, {
        attributes: true,
        attributeFilter: ['contenteditable'],
        childList: true,
        subtree: true,
      })
      return () => observer.disconnect()
    }, [syncAccessibility])

    return (
      <LexicalComposerInput placeholder={placeholder} ref={containerRef} {...props}>
        <DmpChatLexicalBridgePlugin disabled={disabled} sendDisabled={sendDisabled} />
        {children}
      </LexicalComposerInput>
    )
  }
)

DmpChatLexicalInput.displayName = 'DmpChatLexicalInput'
